import { useState, useMemo } from 'react';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useMyReferralCommissions } from '@/api/account';
import { paginationView } from '@/pages/customers/pagination-summary';
import { buildCommissionColumns } from '@/pages/affiliates/detail/commissions-tab';

const PAGE_SIZE = 20;

/**
 * Employee self-view: Commissions tab.
 * Reuses column definitions from the manager commissions-tab with linkInvoices=false
 * (invoice IDs shown as plain text — /invoices/:id is manager-only).
 * Customer links remain active (/customers/:id is accessible to employees).
 * Data comes from `/employees/current/referral-commissions`.
 */
export function ReferralCommissionsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useMyReferralCommissions({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // linkInvoices=false: /invoices/:id is manager-only, render invoice IDs as plain text
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildCommissionColumns(false), []);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load commissions"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.commission_id)}
      emptyState={
        <EmptyState
          title="No commissions yet"
          description="Your commission records will appear here."
        />
      }
      footer={
        isLoading ? null : (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        )
      }
    />
  );
}
